/**
 * @file SidebarMenuItemWithSub.tsx
 * @module _admin/layout/components/sidebar/sidebar-menu/SidebarMenuItemWithSub
 * @description Renders the SidebarMenuItemWithSub navigation component used to compose the admin sidebar menu.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.5
 */

import React from 'react';
import clsx from 'clsx';
import { useLocation } from 'react-router';
import { checkIsActive, KTSVG, WithChildren } from '../../../../helpers';
import { useLayout } from '../../../core';
import DefaultMenuIconSVG from '../../../../assets/media/dashboard/master.svg';
type Props = {
  to: string;
  title: string;
  icon?: string;
  fontIcon?: string;
  hasBullet?: boolean;
  activeMatch?: string;
  className?: string;
};
const SidebarMenuItemWithSub: React.FC<Props & WithChildren> = ({
  children,
  to,
  title,
  icon = DefaultMenuIconSVG,
  fontIcon,
  hasBullet,
  activeMatch,
  className,
}) => {
  const { pathname } = useLocation();
  const isActive = checkIsActive(pathname, to) || (activeMatch ? checkIsActive(pathname, activeMatch) : false);
  const { config } = useLayout();
  const { app } = config;

  return (
    <div
      className={clsx(
        'menu-item',
        { 'here show': isActive },
        'menu-accordion',
        className
      )}
      data-kt-menu-trigger="click"
    >
      <span className="menu-link">
        {hasBullet && (
          <span className="menu-bullet">
            <span className="bullet bullet-dot"></span>
          </span>
        )}
        {icon && app?.sidebar?.default?.menu?.iconType === 'svg' && (
          <span className="menu-icon">
            <KTSVG
              path={icon}
              className="svg-icon-2"
            />
          </span>
        )}
        {fontIcon && app?.sidebar?.default?.menu?.iconType === 'font' && (
          <i className={clsx('bi fs-3', fontIcon)}></i>
        )}
        <span className="menu-title">{title}</span>
        <span className="menu-arrow"></span>
      </span>
      <div
        className={clsx('menu-sub menu-sub-accordion', {
          'menu-active-bg': isActive,
        })}
      >
        {children}
      </div>
    </div>
  );
};
export { SidebarMenuItemWithSub };
